// MA03 권한관리 2.0 API (LOGISPRD-9616~9618, /api/v1/authorities)
// - ITEM 조회: GET /{authCode}/menus — MOMS_AUTH_INFO ⋈ MOMS_MENU_INFO 단일 쿼리
// - 저장: POST(생성, 전체 메뉴 기본 매핑 자동) / PUT /{authCode}(권한명) / PUT /{authCode}/menus(매핑 수정)
// - 삭제: DELETE /{authCode} — 사용 중 권한은 BE 가 400 (AuthorityInUseException)
import { useMutation, useQuery, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query'
import request from '@/utils/request'
import { formatDateTime } from '@/pages/master/userManagement/constants'
import { AuthMenuRow } from '../consts'

interface AuthorityMenuItem {
  shipperCode: string
  authCode: string
  authName: string
  menuCode: string
  menuName: string
  menuType: string // FOLD(상위) | PROG(하위)
  menuTree: string
  menuOrder: string
  menuActive: boolean
  buttonLevel: string
  filterActive: boolean
  // LLD 는 ISO-8601 문자열 계약이지만 실제 BE 직렬화는 [yyyy,MM,dd,HH,mm,ss] 배열
  // (@JsonFormat 부재 — 유저관리와 동일 사안이라 formatDateTime 으로 배열/문자열 겸용 처리)
  insDate?: string | number[] | null
  insBy?: string
  updDate?: string | number[] | null
  updBy?: string
}

const yn = (b: boolean) => (b ? 'Y' : 'N')
const fmtDate = (v?: string | number[] | null) => (v == null ? '' : formatDateTime(v))

const toRow = (m: AuthorityMenuItem): AuthMenuRow => ({
  AUTH_CD: m.authCode,
  MENU_LV: m.menuType === 'FOLD' ? 1 : 2,
  MENU_TREE: m.menuTree,
  MENU_CD: m.menuCode,
  MENU_NM: m.menuName,
  MENU_ACT: yn(m.menuActive),
  BUTTON_LV: m.buttonLevel,
  FILTER_YN: yn(m.filterActive),
  INS_DATE: fmtDate(m.insDate),
  INS_BY: m.insBy ?? '',
  UPD_DATE: fmtDate(m.updDate),
  UPD_BY: m.updBy ?? '',
})

export const authorityMenusQueryKeys = {
  all: ['AUTHORITY_MENUS'] as const,
  list: (shipperCode: string, authCode: string) => [...authorityMenusQueryKeys.all, shipperCode, authCode] as const,
}

export const useGetAuthorityMenus = (shipperCode: string, authCode: string): UseQueryResult<AuthMenuRow[], Error> =>
  useQuery({
    queryKey: authorityMenusQueryKeys.list(shipperCode, authCode),
    queryFn: async () => {
      const res = await request.get<ApiResponseV2<{ items: AuthorityMenuItem[] }>, { shipperCode: string }>(
        `/api/v1/authorities/${encodeURIComponent(authCode)}/menus`,
        { shipperCode },
      )
      return (res.data?.items ?? []).map(toRow)
    },
    enabled: !!shipperCode && !!authCode,
    meta: { noErrorMessage: true }, // 실패 안내는 화면이 새 디자인 알럿으로 한 번 낸다(usePboQueryErrorToast → notice.failure — 옛 전역 알럿 대신)
  })

// ---- 저장/삭제 ----

export interface SaveAuthorityHeadReq {
  shipperCode: string
  authCode: string
  authName: string
  isNew: boolean
}

// 헤드 저장 — 신규 행은 생성(POST), 기존 행은 권한명 변경(PUT).
// 기존 행의 화주/권한코드(복합키) 변경 API 는 없다 — 권한명만 반영된다.
export const useSaveAuthorityHead = (): UseMutationResult<number, Error, SaveAuthorityHeadReq[]> =>
  useMutation({
    mutationFn: async (rows: SaveAuthorityHeadReq[]) => {
      for (const row of rows) {
        if (row.isNew) {
          await request.post<ApiResponseV2<unknown>, { shipperCode: string; authCode: string; authName: string }>('/api/v1/authorities', {
            shipperCode: row.shipperCode,
            authCode: row.authCode,
            authName: row.authName,
          })
        } else {
          await request.put<ApiResponseV2<unknown>, { shipperCode: string; authName: string }>(
            `/api/v1/authorities/${encodeURIComponent(row.authCode)}`,
            { shipperCode: row.shipperCode, authName: row.authName },
          )
        }
      }
      return rows.length
    },
  })

export const useDeleteAuthorities = (): UseMutationResult<number, Error, { shipperCode: string; authCode: string }[]> =>
  useMutation({
    mutationFn: async (rows: { shipperCode: string; authCode: string }[]) => {
      for (const row of rows) {
        await request.delete<ApiResponseV2<unknown>>(`/api/v1/authorities/${encodeURIComponent(row.authCode)}`, null, {
          shipperCode: row.shipperCode,
        })
      }
      return rows.length
    },
  })

export interface SaveAuthorityMenuReq {
  menuCode: string
  menuActive: boolean
  buttonLevel: string
  filterActive: boolean
}

// 메뉴 매핑 저장 — 미존재 menuCode 포함 시 BE 가 전체 롤백(400). 신규 매핑 추가 API 는 없다.
export const useSaveAuthorityMenus = (): UseMutationResult<number, Error, { shipperCode: string; authCode: string; items: SaveAuthorityMenuReq[] }> =>
  useMutation({
    mutationFn: async (req: { shipperCode: string; authCode: string; items: SaveAuthorityMenuReq[] }) => {
      await request.put<ApiResponseV2<unknown>, { shipperCode: string; items: SaveAuthorityMenuReq[] }>(
        `/api/v1/authorities/${encodeURIComponent(req.authCode)}/menus`,
        { shipperCode: req.shipperCode, items: req.items },
      )
      return req.items.length
    },
  })
